import { useGetContentListQuery } from '@api/api';
import { ContentRequest, EpiBlock, EpiContent, EpiImage } from '@api/types';
import {
  BlockContainer,
  BlockWrapper,
  DataLoadingProgress,
  EditPanel,
  GenericError,
  Html,
  Image,
  SmartLink,
  Title,
} from '@components';
import { Box, Grid, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import { breakpoints } from '@themes/theme.helpers';
import { Fragment } from 'react';

const MAX_ITEMS_PER_ROW = 4;
const SPACING = 4;

const MediaBlockWrapper = styled(Box)<MediaContainerProps>`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 36px 0;
  .MuiGrid-item {
    flex: ${(p) => (p.singlerow ? '1' : 'unset')};
  }

  .MuiGrid-root {
    flex-direction: ${(p) => (p.singlerow ? 'column' : 'row')};
    ${breakpoints.up('md')} {
      flex-direction: row;
    }
  }

  ${breakpoints.up('xs')} {
    min-height: 250px;
  }

  ${breakpoints.up('lg')} {
    min-height: 675px;
  }

  ${breakpoints.up('xxl')} {
    min-height: 700px;
  }

  img.epi-background-image {
    object-fit: cover;
    position: absolute;
    height: 100%;
    width: 100%;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    color: transparent;
    z-index: -1;
  }
`;

const CustomText = styled(Typography)`
  font-size: 24px !important;
  text-align: center !important;
  font-weight: 800 !important;
  margin-bottom: 8px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  display: -webkit-box;
`;

const MediaContainer = styled(Box)<MediaContainerProps>`
  width: 100%;
  height: ${(p) => (p.singlerow ? 'auto' : '260px')};
  overflow: hidden;
  position: relative;

  img,
  video {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
`;

type MediaContainerProps = {
  singlerow: string | undefined;
};

type MediaListBlockProps = {
  backgroundImage?: EpiImage;
  content?: EpiBlock[];
  title?: string;
  textColor?: 'default' | 'black' | 'white';
  disableEditPanel?: boolean;
  links?: EpiBlock[];
  text?: string;
  displayFormat?: 'single' | 'multiple';
};

export const MediaListBlock = ({
  backgroundImage,
  content,
  title,
  textColor,
  disableEditPanel = false,
  text,
  displayFormat = 'multiple',
}: MediaListBlockProps) => {
  const Block = (
    <EditPanel
      disableEditPanel={disableEditPanel}
      properties={['title', 'content', 'textColor', 'text', 'backgroundImage']}
    />
  );
  const theme = useTheme();
  const isBreakpointSmall = useMediaQuery(theme.breakpoints.down('md'));
  const isBreakpointLarge = useMediaQuery(theme.breakpoints.up('lg'));
  const displayItemsInSingleRow = displayFormat === 'single';

  const numberOfItems = (Array.isArray(content) && content?.length) || 0;
  const gridLg = displayItemsInSingleRow ? [numberOfItems] : distributeItemsLg(numberOfItems);
  const gridMd = distributeItemsMd(numberOfItems);
  const grid = isBreakpointLarge ? gridLg : gridMd;
  const hasMaxItemsPerRow = grid.includes(MAX_ITEMS_PER_ROW);

  const references = content
    ?.map((x) => x?.contentLink?.id)
    .filter((x) => x !== undefined) as number[];

  const request: ContentRequest = { references, expand: '*' };
  const response = useGetContentListQuery(request);
  const { data, isLoading, isSuccess, isError } = response;

  return (
    <>
      {Block}
      <MediaBlockWrapper singlerow={displayItemsInSingleRow ? 'true' : undefined}>
        {backgroundImage && <Image image={backgroundImage} imgClassName='epi-background-image' />}
        <BlockContainer>
          <Title color={textColor} sx={{ textAlign: 'center' }}>
            {title}
          </Title>
          {text && (
            <Box mt={4}>
              <Html html={text} />
            </Box>
          )}
          {isLoading && <DataLoadingProgress />}
          {isSuccess && data ? (
            <Grid container spacing={SPACING} justifyContent='center' mt={4}>
              {grid.map((itemsPerRow, rowIndex) => {
                const currentItems = getContent({ rowIndex, content: data, itemsPerRow: grid });
                const columnWidth = hasMaxItemsPerRow ? 3 : 4;
                return (
                  <Fragment key={`row-${rowIndex}`}>
                    {itemsPerRow === MAX_ITEMS_PER_ROW ? (
                      currentItems?.map((itemContent, itemIndex) => (
                        <Grid
                          key={`grid-${rowIndex}-${itemIndex}`}
                          item
                          xs={12}
                          sm={6}
                          md={columnWidth}
                          display='flex'
                          flexWrap='wrap'
                          justifyContent='center'
                          alignSelf={{ xs: 'center', md: 'self-end' }}
                          flexDirection='column'
                        >
                          <BlockWrapper
                            blockData={itemContent}
                            wrapperComponent={(props) => (
                              <ContentItem
                                textColor={textColor}
                                displayItemsInSingleRow={displayItemsInSingleRow}
                                {...props}
                              />
                            )}
                          />
                        </Grid>
                      ))
                    ) : isBreakpointSmall ? (
                      currentItems?.map((itemContent, itemIndex) => (
                        <Grid
                          key={`grid-${rowIndex}-${itemIndex}`}
                          item
                          xs={12}
                          sm={6}
                          md={columnWidth}
                          display='flex'
                          flexWrap='wrap'
                          justifyContent='center'
                          alignSelf={{ xs: 'center', md: 'self-end' }}
                          flexDirection='column'
                        >
                          <BlockWrapper
                            blockData={itemContent}
                            wrapperComponent={(props) => (
                              <ContentItem
                                textColor={textColor}
                                displayItemsInSingleRow={displayItemsInSingleRow}
                                {...props}
                              />
                            )}
                          />
                        </Grid>
                      ))
                    ) : (
                      <Grid
                        container
                        item
                        xs={12}
                        justifyContent='center'
                        spacing={SPACING}
                        key={`grid-container-${rowIndex}`}
                      >
                        {currentItems?.map((itemContent, itemIndex) => (
                          <Grid
                            key={`grid-${rowIndex}-${itemIndex}`}
                            item
                            xs={12}
                            sm={6}
                            md={columnWidth}
                            display='flex'
                            flexWrap='wrap'
                            justifyContent='center'
                            alignSelf={{ xs: 'center', md: 'self-end' }}
                            flexDirection='column'
                          >
                            <BlockWrapper
                              blockData={itemContent}
                              wrapperComponent={(props) => (
                                <ContentItem
                                  textColor={textColor}
                                  displayItemsInSingleRow={displayItemsInSingleRow}
                                  {...props}
                                />
                              )}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Fragment>
                );
              })}
            </Grid>
          ) : null}
          {isError && <GenericError />}
        </BlockContainer>
      </MediaBlockWrapper>
    </>
  );
};

const ContentItem = ({ block, textColor, displayItemsInSingleRow }: EpiBlock) => {
  const isVisualLink = block?.contentType?.includes('VisualLinkBlock');
  const mediaUrl = isVisualLink ? block?.thumbnail?.url : block?.url;
  const mediaType = isVisualLink ? block?.thumbnail?.expanded?.contentType : block?.contentType;
  const mediaAlt = isVisualLink ? block?.thumbnail?.expanded?.altText : block?.altText;
  const mediaMimeType = isVisualLink ? block?.thumbnail?.expanded?.mimeType : block?.mimeType;
  const linkUrl = isVisualLink ? block?.link?.href : undefined;
  const imageConfig = isVisualLink ? block?.thumbnail?.expanded?.imageConfig : block.imageConfig;
  const title = isVisualLink ? block?.thumbnail?.expanded?.title : block?.title;

  const mediaBox = (
    <Box height={displayItemsInSingleRow ? 'auto' : 260} className='media-box'>
      <MediaContainer
        singlerow={displayItemsInSingleRow ? 'true' : undefined}
        className='media-container'
      >
        {mediaType?.includes('Video') ? (
          <video controls>
            <source src={mediaUrl} type={mediaMimeType} />
          </video>
        ) : (
          <Image alt={mediaAlt} image={mediaUrl} {...imageConfig} />
        )}
      </MediaContainer>
    </Box>
  );
  return (
    <>
      {title && <CustomText color={textColor}>{title}</CustomText>}
      {linkUrl ? <SmartLink href={linkUrl}>{mediaBox}</SmartLink> : mediaBox}
    </>
  );
};

type CurrentContentProps = {
  rowIndex: number;
  content?: EpiContent;
  itemsPerRow: number[];
};

const getContent = ({ rowIndex, content, itemsPerRow }: CurrentContentProps) => {
  const startIndex = itemsPerRow.slice(0, rowIndex).reduce((acc, val) => acc + val, 0);
  const endIndex = startIndex + itemsPerRow[rowIndex];
  return Array.isArray(content) ? content.slice(startIndex, endIndex) : undefined;
};

function distributeItemsLg(n: number) {
  let results = [];

  while (n > 4) {
    if (n % 4 === 1 || n % 4 === 2) {
      results.push(3);
      n -= 3;
    } else {
      results.push(4);
      n -= 4;
    }
  }

  if (n > 0) {
    results.push(n);
  }

  return results.sort((a, b) => b - a);
}

function distributeItemsMd(n: number) {
  let results = [];
  while (n > 2) {
    if (n === 4) {
      results.push(2, 2);
      return results;
    }
    results.push(3);
    n -= 3;
  }

  if (n === 2) {
    results.push(2);
  } else if (n === 1) {
    results[results.length - 1] = 2;
    results.push(2);
  }

  return results;
}
